<template>
  <div class="wechat-user-manage">
    <basic-container>
      <WeChatUserManageSearch
        :tagOptions="tagOptions"
        :leboTagOptions="leboTagOptions"
        :searchForm="searchForm"
        @search="handleSearch"
        @batchEdit="handleBatchEdit"
      />
      <WeChatUserManageTable
        :tableData="tableData"
        :loading="loading"
        @view="handleView"
        @edit="handleEdit"
        @record="handleRecord"
      />
      <div class="pagination-container">
        <ElementPagination
          :current-page="page.current"
          :page-size="page.size"
          :page-sizes="[10, 20, 30, 50, 100]"
          :total="page.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </basic-container>

    <!-- Modals -->
    <BatchEditDialog
      :visible.sync="batchEditVisible"
      :tagOptions="tagOptions"
      :leboTagOptions="leboTagOptions"
      @submit="handleBatchSubmit"
      @close="handleBatchCloseDialog"
    />
    <UserDetailDialog
      :visible.sync="userDetailVisible"
      :rowData="currentRowData"
      :mode="userDetailMode"
      :tagOptions="tagOptions"
      :leboTagOptions="leboTagOptions"
      @submit="handleDetailSubmit"
    />
    <RecordDialog
      :visible.sync="recordVisible"
      :openid="recordOpenid"
    />
  </div>
</template>

<script>
import WeChatUserManageSearch from './components/search.vue'
import WeChatUserManageTable from './components/table.vue'
import BatchEditDialog from './components/BatchEditDialog.vue'
import UserDetailDialog from './components/UserDetailDialog.vue'
import RecordDialog from './components/RecordDialog.vue'
import ElementPagination from '@/components/ElementPagination'
import { 
    getWeChatUserList,
    getWeChatTagsOption
} from '@/api/operationsManage/weChatUserManage'
import { getLeboTagOptions } from '@/api/operationsManage/weChatUserManage'
export default {
  name: 'WeChatUserManage',
  components: {
    WeChatUserManageSearch,
    WeChatUserManageTable,
    ElementPagination,
    BatchEditDialog,
    UserDetailDialog,
    RecordDialog
  },
  data() {
    return {
      tagOptions: [],
      leboTagOptions: [],
      tableData: [],
      loading: false,
      searchForm: {},
      page: {
        current: 1,
        size: 100,
        total: 0
      },
      batchEditVisible: false,
      userDetailVisible: false,
      currentRowData: null,
      userDetailMode: 'view', // 'view' | 'edit'
      recordVisible: false,
      recordOpenid: ''
    }
  },
  created() {
    this.getWeChatTagsOption()
    this.getLeboTagOptions()
    this.getTableData()
  },
  activated() {
    // 从 sessionStorage 读取跳转参数
    const searchParamsStr = sessionStorage.getItem('weChatUserManage_searchParams')
    if (searchParamsStr) {
      try {
        const searchParams = JSON.parse(searchParamsStr)
        this.searchForm = {
          openid: searchParams.openid || '',
          unionid: searchParams.unionid || '',
          keyword: searchParams.keyword || ''
        }
        // 使用完后立即清除，避免重复使用
        sessionStorage.removeItem('weChatUserManage_searchParams')
      } catch (error) {
        console.error('解析搜索参数失败:', error)
        sessionStorage.removeItem('weChatUserManage_searchParams')
      }
    } else {
      this.searchForm = {}
    }
    this.getTableData()
  },
  methods: {
    async getWeChatTagsOption() {
      const res = await getWeChatTagsOption()
      this.tagOptions = res.data.data.map(item => ({
        id: item.vxTagId,
        tagName: item.vxTagName
      })) || []
    },
    async getLeboTagOptions() {
      const res = await getLeboTagOptions()
      this.leboTagOptions = res.data.data.map(item => ({
        id: item.id,
        tag: item.name
      })) || []
    },
    handleSearch(form) {
      this.searchForm = form
      this.page.current = 1
      this.getTableData()
    },
    handleBatchEdit() {
      this.batchEditVisible = true
    },
    handleBatchSubmit() {
      this.$message.success('批量编辑提交成功')
    },
    handleView(row) {
      this.currentRowData = row
      this.userDetailMode = 'view'
      this.userDetailVisible = true
    },
    handleEdit(row) {
      this.currentRowData = row
      this.userDetailMode = 'edit'
      this.userDetailVisible = true
    },
    handleDetailSubmit() {
      this.getTableData()
    },
    handleRecord(row) {
      this.recordOpenid = row.openid
      this.recordVisible = true
    },
    handleSizeChange(val) {
      this.page.size = val
      this.getTableData()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getTableData()
    },
    handleBatchCloseDialog() {
      this.batchEditVisible = false
      this.getTableData()
    },
    async getTableData() {
      this.loading = true
      try {
        const params = {
          current: this.page.current,
          size: this.page.size
        }
        
        // 映射搜索表单字段到后端接口参数
        if (this.searchForm.followTimeRange && this.searchForm.followTimeRange.length === 2) {
          params.subStartTime = this.searchForm.followTimeRange[0]
          params.subEndTime = this.searchForm.followTimeRange[1]
        }
        if (this.searchForm.syncTimeRange && this.searchForm.syncTimeRange.length === 2) {
          params.synStartTime = this.searchForm.syncTimeRange[0]
          params.synEndTime = this.searchForm.syncTimeRange[1]
        }
        if (this.searchForm.keyword) {
          params.uid1 = this.searchForm.keyword
        }
        if (this.searchForm.openid) {
          params.uid2 = this.searchForm.openid
        }
        if (this.searchForm.unionid) {
          params.unionid = this.searchForm.unionid
        }
        if (this.searchForm.wechatTagId) {
          params.vxUserTag = this.searchForm.wechatTagId
        }
        if (this.searchForm.leboTagId) {
          params.lbUserTag = this.searchForm.leboTagId
        }
        
        const res = await getWeChatUserList(params)

        // 映射后端字段到前端使用的字段
        this.tableData = (res.data.data.records || []).map(item => ({
          ...item,
          openid: item.appOpenid || '', // 服务号_openid
          unionid: item.unionid || '', // 微信_unionid
          leboUuid: item.uuid || '', // 乐播_uuid
          phone: item.mobile || '', // 手机号
          appOpenid: item.vxOpenid || '', // App_openid
          miniProgramOpenid: item.miniOpenid || '', // 小程序_openid
          nickname: item.nickName || '', // 昵称
          city: item.city || '', // 城市
          followStatus: item.subscribeStatus === 1 ? '已关注' : '未关注', // 关注状态
          wechatFollowTime: item.subscribeTime || '', // 服务号关注时间
          syncTime: item.updateTime || '', // 最新同步时间
          leboTagsText: Array.isArray(item.lbUserTag) && item.lbUserTag.length > 0
            ? item.lbUserTag.map(tag => tag.name || tag).join('、')
            : '-', // 乐播用户标签文本
          wechatTagsText: Array.isArray(item.vxUserTag) && item.vxUserTag.length > 0
            ? item.vxUserTag.map(tag => tag.vxTagName || tag.name || tag).join('、')
            : '-', // 微信用户标签文本
          lbUserTag: item.lbUserTag || [], // 保留原始数组，可能其他地方会用到
          vxUserTag: item.vxUserTag || [], // 保留原始数组，可能其他地方会用到
          lbUserTagsAlreadyExistText: Array.isArray(item.lbUserNameList) && item.lbUserNameList.length > 0
            ? item.lbUserNameList.join('、')
            : '-', // 用户已存在的乐播标签文本
        }))
        this.page.total = res.data.data.total || 0
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style>

</style>
